// A labelled control, with its hint and its error ACTUALLY attached to it.
//
// shadcn's `Field` is layout: it stacks a label, a control and a description and gives
// them the right rhythm. What it does not do is associate them — `FieldDescription` is
// a plain `<p>`, so a screen reader announces the label and nothing else, and the
// caveat that says what the field will do with the value is read by nobody.
//
// Wiring it at each call site is the version that goes wrong, and goes wrong silently:
// nothing renders differently, no test fails unless somebody wrote one, and the gap
// shows up only for the people who cannot see the hint sitting underneath. So it is a
// component instead — the ids are generated here and handed to the control, and a
// caller cannot forget what it never had to remember.
//
// The control is a RENDER PROP rather than a cloned child. Cloning would mean reaching
// into an element somebody else composed and overwriting props they may have set; this
// hands the ids over and lets the caller spread them where they belong.

import type { ReactNode } from "react";
import { useId } from "react";
import { FieldDescription, FieldError, FieldLabel, Field as Shell } from "@/components/ui/field";
import { Explains } from "./explains";

/** The attributes a control needs to be described by its own hint and error. */
export interface FieldIds {
  readonly id: string;
  readonly "aria-describedby": string | undefined;
  readonly "aria-invalid": boolean | undefined;
}

interface LabelledProps {
  readonly label: string;
  /** What this field does with the value, or what it will refuse. Announced. */
  readonly hint?: string;
  /**
   * WHAT RIKSU READ, in riksu's own convention. Announced, and never an error.
   *
   * Its own slot rather than a second use of `hint`, because it answers a different
   * question: a hint describes the FIELD and is true before anybody types, and this
   * describes the VALUE and changes with every keystroke. Several fields legitimately
   * need both — the expense amount carries its category ceiling in the hint — so one
   * slot doing both jobs would mean choosing which of them to drop.
   *
   * # Every money input in this product must carry one
   *
   * `parseMoney` INFERS a magnitude from separators that mean opposite things in the two
   * locales: `15.000,00` is fifteen thousand under `tr` and fifteen under `en`, and
   * `15,000.00` is the same pair the other way round. It is symmetric, it has always
   * been there, and making English the source makes the wrong half the ORDINARY case —
   * the reader is a Turkish person whose hands type `15.000,00` into a form that has
   * just stopped expecting it. Three orders of magnitude, silently, on figures that
   * include the wage the 4857 md. 35 quarter is computed from.
   *
   * The repository's own rule answers it: A VALUE MAY ONLY BE INFERRED IF THE PERSON CAN
   * SEE WHAT WAS INFERRED. Rendering the parse back beside the input is the only moment
   * the mistake is still correctable.
   *
   * It is `aria-describedby` rather than a live region on purpose: a figure re-announced
   * on every keystroke is noise, and the description is read when somebody arrives at or
   * returns to the field, which is when they are checking it.
   */
  readonly readBack?: string;
  /** riksu's own sentence when the value was refused. Announced, and marks invalid. */
  readonly error?: string;
  /**
   * WHY RIKSU ASKS — on hover and on focus, never on the screen.
   *
   * The one member that moves a sentence off the surface, and the whole of what a call
   * site pays: adding an explanation is a line here, never a component wrapped by hand.
   *
   * # What may go in it, and the test is not a matter of taste
   *
   * A tooltip is unreachable on a touch screen — Base UI's trigger is `mouseOnly` for
   * hover and gates focus on `matchesFocusVisible`, both read out of its source — so
   * **putting a fact here is a decision that the fact is NOT REQUIRED.** The vocabulary
   * (*zimmet*, *avans*, *özlük dosyası*), a statutory citation, the reasoning behind a
   * derived figure and why a control is shaped the way it is all qualify: useful once,
   * noise on every later visit.
   *
   * What does NOT: a format, a bound, what riksu will REFUSE, what to leave empty, or
   * anything that changes what somebody types. That is `hint`, and it stays visible.
   *
   * Several fields legitimately need both, and the split is per CLAUSE — `hint` keeps
   * "Two letters, ISO 3166 — TR, DE, NL" while `why` takes "never defaulted: a silent TR
   * files a foreign subsidiary under Turkish rules". It was measured before it was built,
   * and there is deliberately no helper that derives the split: of 87 hints, 47 are
   * multi-sentence and in six of those the LAST sentence is the actionable half.
   *
   * The explanation still reaches a screen reader on every device, because `Explains`
   * carries it on an always-mounted `sr-only` span rather than on the portalled popup.
   */
  readonly why?: string;
  readonly children: (ids: FieldIds) => ReactNode;
}

export function Labelled({ label, hint, readBack, error, why, children }: LabelledProps) {
  const base = useId();
  const displayedHint = hint !== error ? hint : undefined;
  const hintId = displayedHint ? `${base}-hint` : undefined;
  const readBackId = readBack ? `${base}-read` : undefined;
  const errorId = error ? `${base}-error` : undefined;
  // Distinct messages stay in reading order. When the refusal repeats the hint,
  // its error node supplies that sentence once, including to assistive technology.
  const describedBy = [hintId, readBackId, errorId].filter(Boolean).join(" ") || undefined;

  return (
    <Shell data-invalid={error ? "" : undefined}>
      {/* THE TRIGGER IS A SIBLING OF THE LABEL, NEVER A CHILD OF IT. `<label>`'s content
          model admits no descendant labelable element other than its own control, and a
          `<button>` is labelable — so nesting it is invalid HTML, and the browser
          behaviour it produces is a press that focuses the input instead of opening the
          explanation. The row wrapper exists only where there is something to put in it,
          so a field with no `why` renders exactly the markup it did before. */}
      {why ? (
        <div className="flex w-fit items-center">
          <FieldLabel htmlFor={base}>{label}</FieldLabel>
          <Explains about={label} why={why} />
        </div>
      ) : (
        <FieldLabel htmlFor={base}>{label}</FieldLabel>
      )}
      {children({
        id: base,
        "aria-describedby": describedBy,
        // `aria-invalid` is left OFF rather than set to false: a control that has not
        // been submitted is not valid, it is unanswered, and announcing "valid" for
        // every empty field is noise.
        "aria-invalid": error ? true : undefined,
      })}
      {displayedHint ? <FieldDescription id={hintId}>{displayedHint}</FieldDescription> : null}
      {readBack ? (
        // `tabular-nums` so the figure does not reflow under the cursor as digits are
        // typed, which is the thing that makes a live read-back feel unstable.
        <FieldDescription id={readBackId} className="tabular-nums">
          {readBack}
        </FieldDescription>
      ) : null}
      {error ? <FieldError id={errorId}>{error}</FieldError> : null}
    </Shell>
  );
}
